import React from 'react';
import PropTypes from 'prop-types';
import { NamedLink } from '../../../../components';
import { formatLocationShort } from '../../../../util/stateNames';
import { LISTING_TYPES, PRICE_VERIFICATION_STATUSES } from '../../../../util/listingTypes';
import verifiedPriceIcon from '../../../../assets/Verified_Price_icon.svg';
import css from './ListingCard.module.css';

// Helper function to format date from YYYY-MM-DD to Month Day format
const formatDate = dateString => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();

  return `${month} ${day}`;
};

const formatWholeDollarPrice = amount => {
  const numericAmount = Number(amount || 0);
  return `$${Math.round(numericAmount).toLocaleString('en-US')}`;
};

const ListingCard = ({ listing }) => {
  const dateTimeText = [listing.date ? formatDate(listing.date) : null, listing.time]
    .filter(Boolean)
    .join(' • ');
  const locationText = formatLocationShort(listing.location) || 'Location TBD';
  const priceText = listing.priceLabel || formatWholeDollarPrice(listing.price);
  const ctaText = listing.ctaLabel || 'Buy now \u2192';
  const isTicketSaleListing = listing.listingType === LISTING_TYPES.TICKET_SALE;
  const priceVerificationStatus =
    listing.priceVerificationStatus || PRICE_VERIFICATION_STATUSES.UNVERIFIED;
  const isPriceVerified = priceVerificationStatus === PRICE_VERIFICATION_STATUSES.VERIFIED;
  const showPriceVerificationBadge = isTicketSaleListing && isPriceVerified;

  return (
    <NamedLink
      className={css.cardLink}
      name="ListingPage"
      params={{ id: listing.id, slug: listing.slug }}
    >
      <div className={`${css.card} ${css.cardLanding}`}>
        <div className={css.cardImage}>
          <img src={listing.image} alt={listing.title} />
          {showPriceVerificationBadge ? (
            <span className={css.priceVerificationBadge} aria-label="Verified price">
              <img
                className={css.priceVerificationBadgeImage}
                src={verifiedPriceIcon}
                alt=""
                aria-hidden="true"
                draggable="false"
              />
            </span>
          ) : null}
        </div>
        <div className={css.cardContent}>
          {dateTimeText ? (
            <div className={css.dateTimeLine}>
              <span className={css.dateTime}>{dateTimeText}</span>
            </div>
          ) : null}
          <div className={css.titleLine}>
            <span className={css.cardTitle}>{listing.title}</span>
          </div>
          <div className={css.detailsLine}>
            <span className={css.venueLocation}>{locationText}</span>
          </div>
          <div className={css.priceLine}>
            <span className={css.priceValue}>{priceText}</span>
            <span className={css.viewListingsLink}>{ctaText}</span>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCard.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    venue: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    priceLabel: PropTypes.string,
    ctaLabel: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    description: PropTypes.string,
    listingType: PropTypes.string,
    priceVerificationStatus: PropTypes.string,
  }).isRequired,
};

export default ListingCard;
